import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Topbar from "../components/layout/Topbar.jsx";
import { Pagination, Modal } from "../components/ui/index.jsx";
import { getCompanies, createCompany } from "../services/companyService.js";

export default function ClientsPage() {
  const navigate = useNavigate();

  // ── Données ──────────────────────────────────
  const [companies, setCompanies] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState("");

  // ── Filtres ───────────────────────────────────
  const [search,       setSearch]       = useState("");
  const [filterActive, setFilterActive] = useState("ALL");
  const [page,         setPage]         = useState(0);

  // ── Modale création ──────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [form,       setForm]     = useState({ name: "", email: "", phone: "", address: "" });
  const [creating,   setCreating] = useState(false);
  const [formError,  setFormError] = useState("");

  // Charge la liste au montage
  useEffect(() => { loadCompanies(); }, []);

  const loadCompanies = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getCompanies();
      const raw = res.data || [];

      // Déduplication côté frontend : si deux companies ont un nom très
      // similaire (même nom normalisé), on garde celle qui a un ateraCustomerId,
      // sinon la première trouvée.
      const normalize = (s) =>
        (s || "").toLowerCase()
          .replace(/[-_]/g, " ")          // tirets → espaces
          .replace(/[éèêë]/g, "e")        // accents e
          .replace(/[àâä]/g, "a")         // accents a
          .replace(/[ùûü]/g, "u")         // accents u
          .replace(/[îï]/g, "i")          // accents i
          .replace(/[ôö]/g, "o")          // accents o
          .replace(/[ç]/g, "c")           // cédille
          .replace(/\s+/g, " ")           // espaces multiples
          .trim();

      const seen = new Map();
      raw.forEach(c => {
        const key = normalize(c.name);
        if (!seen.has(key)) {
          seen.set(key, c);
        } else {
          // On garde celle qui a un ateraCustomerId (vient de la sync)
          const existing = seen.get(key);
          if (!existing.ateraCustomerId && c.ateraCustomerId) {
            seen.set(key, c);
          }
        }
      });

      setCompanies([...seen.values()]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filtrage côté client (instantané)
  const filtered = useMemo(() => {
    return companies.filter(c => {
      if (filterActive === "true"  && !c.active) return false;
      if (filterActive === "false" &&  c.active) return false;
      if (search && !(c.name || "").toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [companies, filterActive, search]);

  // Création d'une nouvelle entreprise
  const handleCreate = async () => {
    setFormError("");
    if (!form.name.trim())  return setFormError("La raison sociale est obligatoire.");
    if (!form.email.trim()) return setFormError("L'email est obligatoire.");
    if (!form.phone.trim()) return setFormError("Le téléphone est obligatoire.");

    setCreating(true);
    try {
      await createCompany(form);
      setShowModal(false);
      setForm({ name: "", email: "", phone: "", address: "" });
      loadCompanies();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const activeCount = companies.filter(c => c.active).length;

  return (
    <>
      <Topbar title="Mes" subtitle="clients"/>
      <div className="page-body">

        {/* En-tête */}
        <div className="page-header">
          <div>
            <div className="page-title">Entreprises <span>clientes</span></div>
            <div className="page-sub">{activeCount} client{activeCount > 1 ? "s" : ""} actif{activeCount > 1 ? "s" : ""} · {companies.length} au total</div>
          </div>
          <div className="page-actions">
            <button className="btn-itm btn-itm-outline"><i className="bi bi-download" /> Export</button>
            <button className="btn-itm btn-itm-primary" onClick={() => setShowModal(true)}>
              <i className="bi bi-plus-lg" /> Nouveau client
            </button>
          </div>
        </div>

        {/* Filtres */}
        <div className="filter-bar">
          <div className="input-group-itm" style={{ width: 220 }}>
            <i className="bi bi-search input-icon" />
            <input className="itm-input" placeholder="Rechercher…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="itm-select" value={filterActive} onChange={e => setFilterActive(e.target.value)}>
            <option value="ALL">Tous les statuts</option>
            <option value="true">Actifs</option>
            <option value="false">Inactifs</option>
          </select>
        </div>

        {/* Chargement */}
        {loading && (
          <div style={{ textAlign: "center", padding: 60, color: "var(--text-3)" }}>
            <i className="bi bi-arrow-repeat spin" style={{ fontSize: 28, opacity: .4 }}/>
            <p style={{ marginTop: 12 }}>Chargement…</p>
          </div>
        )}

        {/* Erreur */}
        {!loading && error && (
          <div style={{ textAlign: "center", padding: 60 }}>
            <i className="bi bi-exclamation-triangle" style={{ fontSize: 28, color: "var(--danger)" }}/>
            <p style={{ color: "var(--danger)", marginTop: 12 }}>{error}</p>
            <button className="btn-itm btn-itm-outline" onClick={loadCompanies} style={{ marginTop: 10 }}>Réessayer</button>
          </div>
        )}

        {/* Table */}
        {!loading && !error && (
          <div className="itm-card anim-fade-up">
            <table className="itm-table">
              <thead>
                <tr>
                  <th>Entreprise</th>
                  <th>Contact</th>
                  <th>Statut</th>
                  <th>Téléphone</th>
                  <th>Depuis</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: "center", padding: 40, color: "var(--text-3)" }}>Aucun client trouvé</td></tr>
                ) : filtered.map(c => (
                  <tr key={c.id} onClick={() => navigate(`/clients/${c.id}`)}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 34, height: 34, borderRadius: 10, background: "var(--accent-dim)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
                          {c.name?.[0]}
                        </div>
                        <div>
                          <div className="cell-primary">{c.name}</div>
                          {c.ateraCustomerId && <div className="cell-sub">Atera #{c.ateraCustomerId}</div>}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: 13, color: "var(--text-1)" }}>{c.contactName || "—"}</div>
                      <div style={{ fontSize: 11, color: "var(--text-3)" }}>{c.email}</div>
                    </td>
                    <td>
                      <span className={`badge-status ${c.active ? "bs-active" : "bs-inactive"}`}>
                        {c.active ? "Actif" : "Inactif"}
                      </span>
                    </td>
                    <td style={{ color: "var(--text-2)" }}>{c.phone || "—"}</td>
                    <td style={{ color: "var(--text-3)", fontSize: 12 }}>
                      {c.createdAt ? new Date(c.createdAt).toLocaleDateString("fr-FR", { month: "short", year: "numeric" }) : "—"}
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button className="btn-icon-sm" title="Voir la fiche" onClick={() => navigate(`/clients/${c.id}`)}>
                          <i className="bi bi-eye" />
                        </button>
                        <button className="btn-icon-sm" title="Voir les tickets" onClick={() => navigate(`/tickets?companyId=${c.id}`)}>
                          <i className="bi bi-ticket-detailed" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} total={filtered.length} pageSize={10} onChange={setPage} />
          </div>
        )}

        {/* Modal nouveau client */}
        {showModal && (
          <Modal title="Nouveau client" onClose={() => setShowModal(false)}
            footer={<>
              <button className="btn-itm btn-itm-outline" onClick={() => setShowModal(false)}>Annuler</button>
              <button className="btn-itm btn-itm-primary" onClick={handleCreate} disabled={creating}>
                {creating
                  ? <><i className="bi bi-arrow-repeat spin"/> Création…</>
                  : <><i className="bi bi-plus-lg" /> Créer le client</>
                }
              </button>
            </>}
          >
            {formError && (
              <div style={{ background: "var(--danger-dim)", border: "1px solid rgba(239,68,68,.25)", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "var(--danger)" }}>
                <i className="bi bi-exclamation-triangle me-2" />{formError}
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label className="itm-label">Raison sociale *</label>
                <input className="itm-input" placeholder="ACME Corp"
                  value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}/>
              </div>
              <div className="form-group">
                <label className="itm-label">Email *</label>
                <input className="itm-input" type="email" placeholder="contact@acme.fr"
                  value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}/>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="itm-label">Téléphone *</label>
                <input className="itm-input" placeholder="01 23 45 67 89"
                  value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}/>
              </div>
              <div className="form-group">
                <label className="itm-label">Adresse</label>
                <input className="itm-input" placeholder="12 rue de la Paix, 75001 Paris"
                  value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}/>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </>
  );
}